import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core'
import { FormBuilder } from '@angular/forms'
import { Destroyable, isSomething, selectAllRentals, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { debounceTime, map, startWith, tap } from 'rxjs/operators'
import { Store, select } from '@ngrx/store'
import * as lodash from 'lodash'
import {
  ChannelReviewSearchParams,
  ReviewRatingCategory,
  ReviewService,
  channelReviewSourceOptions,
} from '@tokeet-frontend/reviews'

@Component({
  selector: 'app-reviews-filters',
  templateUrl: './reviews-filters.component.html',
  styleUrls: ['./reviews-filters.component.scss'],
})
export class ReviewsFiltersComponent extends Destroyable implements OnInit, OnChanges {
  @Input() source: string
  @Output() filtersChange = new EventEmitter<ChannelReviewSearchParams>()

  @Output() refresh = new EventEmitter()

  sourceOptions = channelReviewSourceOptions

  form = this.fb.group({
    sources: [[]],
    rentals: [[]],
    submitted: [],
    includeDeletedInquiries: [false],
    responded: [],
    category: [],
    ratings: [[]],
    reviewerRole: [],
  })

  rentals$ = this.store.pipe(select(selectAllRentals))

  autoCloseDropdownFilters = true
  ratingCategoryOptions: { value: string; name: string }[] = []
  ratingValueOptions = [5, 4, 3, 2, 1].map((value) => ({
    value,
    name: value === 1 ? `${value} Star` : `${value} Stars`,
  }))
  reviewerRolesOptions = [
    { value: 'host', name: $localize`Host` },
    { value: 'guest', name: $localize`Guest` },
  ]

  constructor(private store: Store<any>, private fb: FormBuilder, private reviewService: ReviewService) {
    super()
  }

  ngOnInit(): void {
    this.loadRatingCategories()

    this.form.valueChanges
      .pipe(
        startWith(this.form.getRawValue()),
        debounceTime(1000),
        map(({ submitted, category, ratings, ...filters }) => {
          const normalizedFilters: Record<string, any> = {
            ...filters,
            submittedAfter: submitted?.from,
            submittedBefore: submitted?.to,
          }

          if (category && isSomething(ratings)) {
            normalizedFilters.categoryRating = {
              category,
              ratings,
            }
          } else if (isSomething(ratings)) {
            normalizedFilters.overallRating = ratings
          }

          const sanitizedFilters = lodash.fromPairs(
            lodash
              .toPairs(normalizedFilters)
              .filter(([k, v]) => !lodash.isNil(v) && (lodash.isArray(v) ? !!v.length : true))
          )

          return { ...sanitizedFilters }
        }),
        tap((filters: Record<string, any>) => {
          this.filtersChange.emit(filters)
        }),
        untilDestroy(this)
      )
      .subscribe()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['source']) {
      this.form.patchValue({ sources: this.source ? [this.source] : [] })
    }
  }

  onRefresh() {
    this.refresh.emit()
  }

  onReplied(replied: boolean) {
    const ctrl = this.form.get('isReplied')
    const value = ctrl.value
    if (value === replied) {
      ctrl.reset()
    } else {
      ctrl.setValue(replied)
    }
  }

  private loadRatingCategories() {
    this.reviewService
      .getRatingCategories()
      .pipe(untilDestroy(this))
      .subscribe((options: ReviewRatingCategory[]) => {
        this.ratingCategoryOptions = lodash.sortBy(options, (option) => option.name?.toLowerCase() || option.name || '')
      })
  }
}
